import * as Sentry from '@sentry/react';
import { lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import AuthedContainerV2 from './route-containers/AuthedContainerV2';
import CatchAll from './route-containers/CatchAll';
import UnauthedContainer from './route-containers/UnauthedContainer';

import JoinSignup from './v2/pages/JoinSignup';
import ResetPassword from './v2/pages/ResetPassword';

/* v2 Pages + Components */
import FollowUpBuilder from './v2/pages/FollowUpBuilder';
import ScheduleMail from './v2/pages/ScheduleMail';
import FormBuilder from './v2/pages/FormBuilder';
import Home from './v2/pages/Home';
import Library from './v2/pages/Library';
import MailTemplateEditor from './v2/pages/Automations/Templates/MailTemplateEditor';
import MailSequenceTemplateEditor from './v2/pages/Automations/MailSequenceTemplates/MailSequenceTemplateEditor';
import Logout from './v2/pages/Logout';
import PatientManagementV2 from './v2/pages/PatientManagement';
import PatientsTableV2 from './v2/pages/PatientManagement/Table';
import ClientSessions from './v2/pages/ClientSessions';
import ClientSessionsTable from './v2/pages/ClientSessions/components/Table';
import PatientProfileV2 from './v2/pages/PatientProfile';
import PatientProfileAccountTab from './v2/pages/PatientProfile/Account';
import PatientProfileActivitiesTab from './v2/pages/PatientProfile/Activities';
import PatientProfileSequencesTab from './v2/pages/PatientProfile/Sequences';
import EnrollmentDetails from './v2/pages/PatientProfile/Sequences/EnrollmentDetails';
import PatientProfileDocumentsTab from './v2/pages/PatientProfile/Documents';
import PatientProfileFollowUpsTab from './v2/pages/PatientProfile/FollowUps';
import PatientProfileMessagesTab from './v2/pages/PatientProfile/Messages';
import PatientProfileNotesTab from './v2/pages/PatientProfile/Notes';
import PatientProfileInvoicesTab from './v2/pages/PatientProfile/Invoices';
import PatientProfileSessionsTab from './v2/pages/PatientProfile/Sessions';
import Schedule from './v2/pages/Schedule';
import Payments from './v2/pages/Payments';

import BillingSectionV2 from './v2/components/Billing/BillingSection';
import SettingsV2 from './v2/pages/Settings';
import IntegrationsSection from './v2/pages/Settings/IntegrationsSection';
import AccountSection from './v2/pages/Settings/AccountSection';

import BillingSuccessV2 from './v2/components/Billing/BillingSuccess';
import SubscriptionUpdated from './v2/components/Billing/SubscriptionUpdated';
import usePageViewTracking from './v2/hooks/usePageViewTracking';
import ClientPdfSubmission from './v2/pages/ClientPdfSubmission';
import FormSubmission from './v2/pages/FormSubmission';
import NoteShare from './v2/pages/NoteShare';
import CalendlyIntegration from './v2/pages/Integrations/Calendly';
import CalendlyIntegrationCallback from './v2/pages/Integrations/Calendly/Callback';
import StripeIntegration from './v2/pages/Integrations/Stripe';
import StripeIntegrationCallback from './v2/pages/Integrations/Stripe/Callback';
import Intro from './v2/pages/Intro';
import FollowUpDetails from './v2/pages/PatientProfile/FollowUps/FollowUpDetails';
import ScheduledMailDetails from './v2/pages/PatientProfile/FollowUps/ScheduledMailDetails';
import NoteDetails from './v2/pages/PatientProfile/Notes/NotesDetails';
import NotificationsSection from './v2/pages/Settings/NotificationsSection';
import SignUp from './v2/pages/SignUp';
import StripeCheckoutRedirect from './v2/pages/StripeCheckoutRedirect';
import Subscribe from './v2/pages/Subscribe';
import InvoiceBuilder from './v2/pages/InvoiceBuilder';
import TagsSection from './v2/pages/Settings/TagsSection';
import TeamSection from './v2/pages/Settings/TeamSection';
import DevelopersSection from './v2/pages/Settings/DevelopersSection';
import MyPage from './v2/pages/MyPage';
import PublicStorefront from './v2/pages/PublicPage';
import AuthedPatientContainer from './route-containers/AuthedPatientContainer';
import PatientCatchAll from './route-containers/PatientCatchAll';
import AuthCallback from './v2/pages/AuthCallback';
import Community from './v2/pages/Community';
import CommunityLanding from './v2/pages/CommunityLanding';
import InsightsPage from './v2/pages/Insights';
import LoginRedirect from './v2/pages/LoginRedirect';
import SubscribeEmbed from './v2/pages/SubscribeEmbed';
import PatientLogin from './v2/pages/ClientPortal/PatientLogin';
import UnsupportedLegacyAccount from './v2/pages/UnsupportedLegacyAccount';

import CompleteProviderForm from './v2/pages/CompleteProviderForm';
import CompletePractitionerForm from './v2/pages/CompletePractitionerForm';
import NylasIntegrationCallback from './v2/pages/Integrations/Nylas/Callback';
import NylasIntegration from './v2/pages/Integrations/Nylas';
import ServiceBuilder from './v2/pages/ServiceBuilder';
import PublicService from './v2/pages/PublicService';
import Services from './v2/pages/Services';
import IntegrationsContainer from './v2/components/Containers/IntegrationsContainer';
import AppearanceSection from './v2/pages/Settings/AppearanceSection';
import ClientPortalSection from './v2/pages/Settings/ClientPortalSection';
import Automations from './v2/pages/Automations';

// Client-portal pages are code-split (lazy-loaded) so they ship as separate
// chunks instead of bloating the render-blocking main bundle that every route
// (including the high-traffic /client/follow-ups/* routes) must download before
// first paint. PatientLogin stays eager because it is the unauthenticated entry
// point and the first paint users see. See Sentry issue WEB-P.
const PatientHome = lazy(() => import('./v2/pages/ClientPortal/PatientHome'));
const PatientLoginToken = lazy(
  () => import('./v2/pages/ClientPortal/PatientLoginToken'),
);
const PatientFollowUpDetails = lazy(
  () => import('./v2/pages/ClientPortal/PatientFollowUpDetails'),
);
const PatientMessages = lazy(
  () => import('./v2/pages/ClientPortal/PatientMessages'),
);
const PatientLibrary = lazy(
  () => import('./v2/pages/ClientPortal/PatientLibrary'),
);
const PatientSessions = lazy(
  () => import('./v2/pages/ClientPortal/PatientSessions'),
);
const PatientAccount = lazy(
  () => import('./v2/pages/ClientPortal/PatientAccount'),
);
const CompletePatientForm = lazy(
  () => import('./v2/pages/ClientPortal/CompletePatientForm'),
);
const PatientFiles = lazy(() => import('./v2/pages/ClientPortal/PatientFiles'));
const PatientIntake = lazy(
  () => import('./v2/pages/ClientPortal/PatientIntake'),
);

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

// Named to avoid collisions with React Router components
const AppRoutes: React.FC = () => {
  // Must be used within the Router context
  usePageViewTracking();

  // Allows Commandbar to use React Router to navigate when it links to parts of the app
  // instead of hard refreshing. I think this has to be done here in a useEffect so that
  // we have access to the navigate function and can be sure its initialized.
  // Saving this commented out b/c we're going to need to do something similar for Intercom
  // const navigate = useNavigate();
  // useEffect(() => {
  //   if (navigate) {
  //     const routerFunc = (url: string) => navigate(url);
  //     CommandbarHelpers.addCommandbarRouter(routerFunc);
  //   }
  // }, [navigate]);

  return (
    <SentryRoutes>
      {/* Public routes */}
      <Route path="/pricing-embed" element={<SubscribeEmbed />} />

      {/* Important for AuthCallback to be outside of UnauthedContainer because it changes auth state */}
      <Route path="/auth-callback" element={<AuthCallback />} />

      {/* Unauthenticated routes */}
      <Route element={<UnauthedContainer />}>
        <Route path="/login" element={<LoginRedirect />} />

        <Route path="/reset" element={<ResetPassword />} />
        <Route path="/join">
          <Route index element={<JoinSignup />} />
        </Route>
        <Route path="/sign-up" element={<SignUp />}>
          <Route path=":slug" element={<SignUp />} />
        </Route>

        {/* Client app */}
        <Route path="/login/client" element={<PatientLogin page="email" />} />
        <Route
          path="/login/client/verify/:verificationId"
          element={<PatientLogin page="verify" />}
        />
        <Route path="/client" element={<AuthedPatientContainer />}>
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<PatientHome />} />
          <Route path="login-token/:tokenId" element={<PatientLoginToken />} />
          <Route
            path="intake/:serviceIntakePackageId"
            element={<PatientIntake />}
          />

          <Route path="follow-ups" element={<PatientFollowUpDetails />} />
          <Route
            path="follow-ups/:programFollowUpId"
            element={<PatientFollowUpDetails />}
          />
          <Route path="messages" element={<PatientMessages />} />
          <Route path="library" element={<PatientLibrary />} />
          <Route path="sessions" element={<PatientSessions />} />
          <Route path="files" element={<PatientFiles />} />
          <Route path="account" element={<PatientAccount />} />
          <Route
            path="client-form/:programActivityId"
            element={<CompletePatientForm />}
          />
          <Route path="*" element={<PatientCatchAll />} />
        </Route>
      </Route>

      {/* Storefront */}
      <Route path="/p/:slug">
        <Route index element={<PublicStorefront />} />
        <Route path=":serviceSlug" element={<PublicService />} />
      </Route>

      {/* Client query-token-authenticated routes */}
      <Route path="/file-submission" element={<ClientPdfSubmission />} />
      <Route path="/form-submission" element={<FormSubmission />} />
      <Route path="/note-share" element={<NoteShare />} />

      {/* Authed provider routes (V2 app). Legacy V1 users are gated to
          /unsupported-account inside AuthedContainerV2. */}
      <Route path="/" element={<AuthedContainerV2 />}>
        <Route path="intro" element={<Intro />} />
        <Route path="community-landing" element={<CommunityLanding />} />
        <Route path="insights" element={<InsightsPage />} />
        <Route path="subscribe" element={<Subscribe />} />
        <Route path="stripe-checkout" element={<StripeCheckoutRedirect />} />
        <Route
          path="unsupported-account"
          element={<UnsupportedLegacyAccount />}
        />

        <Route index element={<Navigate to="home" replace />} />
        <Route path="home" element={<Home />} />

        <Route path="follow-ups/new" element={<FollowUpBuilder />} />
        <Route path="schedule-mail" element={<ScheduleMail />} />
        <Route
          path="/:programId/follow-ups/new/"
          element={<FollowUpBuilder />}
        />
        <Route
          path="/:programId/follow-ups/:programFollowUpDraftId"
          element={<FollowUpBuilder />}
        />

        <Route path="intake/new" element={<FollowUpBuilder />} />
        <Route path="/:programId/intake/new/" element={<FollowUpBuilder />} />

        <Route path="clients" element={<PatientManagementV2 />}>
          <Route index element={<Navigate to="active" replace />} />
          <Route path="active" element={<PatientsTableV2 />} />
          <Route path="archived" element={<PatientsTableV2 />} />
        </Route>

        <Route path="client_sessions" element={<ClientSessions />}>
          <Route index element={<Navigate to="pending" replace />} />
          <Route path="pending" element={<ClientSessionsTable />} />
          <Route path="confirmed" element={<ClientSessionsTable />} />
          <Route path="dismissed" element={<ClientSessionsTable />} />
          <Route path="active/:sessionId" element={<ClientSessionsTable />} />
        </Route>

        <Route path="clients/:programId" element={<PatientProfileV2 />}>
          <Route index element={<Navigate to="follow-ups" replace />} />
          <Route path="messages" element={<PatientProfileMessagesTab />} />
          <Route path="follow-ups" element={<PatientProfileFollowUpsTab />}>
            <Route
              path="scheduled/:scheduledSendId"
              element={<ScheduledMailDetails />}
            />
            <Route path=":programFollowUpId" element={<FollowUpDetails />} />
          </Route>
          <Route path="notes" element={<PatientProfileNotesTab />}>
            <Route path=":programNoteId" element={<NoteDetails />} />
          </Route>
          <Route path="activities" element={<PatientProfileActivitiesTab />} />
          <Route path="sequences" element={<PatientProfileSequencesTab />}>
            <Route path=":enrollmentId" element={<EnrollmentDetails />} />
          </Route>
          <Route path="sessions" element={<PatientProfileSessionsTab />} />
          {/* TODO: Renamed to documents, this is just a redirect for backwards-compatibility */}
          <Route
            path="forms-and-assessments"
            element={<Navigate to="../documents" replace />}
          />
          <Route
            path="documents-and-files"
            element={<PatientProfileDocumentsTab />}
          />
          <Route path="account" element={<PatientProfileAccountTab />} />
          <Route path="invoices" element={<PatientProfileInvoicesTab />} />
        </Route>

        <Route path="library" element={<Library />} />
        <Route path="library/standard-assessments" element={<Library />} />
        <Route path="library/discover" element={<Library />} />
        <Route
          path="automations/templates/mail/new"
          element={<MailTemplateEditor />}
        />
        <Route
          path="automations/templates/mail/:templateId/edit"
          element={<MailTemplateEditor />}
        />
        <Route
          path="automations/templates/mail-sequence/new"
          element={<MailSequenceTemplateEditor />}
        />
        <Route
          path="automations/templates/mail-sequence/:templateId/edit"
          element={<MailSequenceTemplateEditor />}
        />
        <Route path="automations/*" element={<Automations />} />
        <Route path="community" element={<Community />} />

        <Route path="library/forms/new" element={<FormBuilder />} />
        <Route path="library/forms/:assessmentId" element={<FormBuilder />} />
        <Route
          path="provider-form/:programAssessmentId"
          element={<CompleteProviderForm />}
        />
        <Route
          path="complete-practitioner-form"
          element={<CompletePractitionerForm />}
        />

        {/* Redirect for backwards-compatibility */}
        <Route path="schedule" element={<Navigate to="/calendar" replace />} />
        <Route path="calendar" element={<Schedule />} />

        <Route path="payments">
          <Route index element={<Payments />} />
          <Route path="new" element={<InvoiceBuilder />} />
        </Route>

        <Route path="settings" element={<SettingsV2 />}>
          <Route index element={<AccountSection />} />
          <Route path="team" element={<TeamSection />} />
          <Route path="notifications" element={<NotificationsSection />} />
          <Route path="billing" element={<BillingSectionV2 />} />
          <Route path="integrations" element={<IntegrationsSection />} />
          <Route path="tags" element={<TagsSection />} />
          <Route path="appearance" element={<AppearanceSection />} />
          <Route path="client-portal" element={<ClientPortalSection />} />
          <Route path="developers" element={<DevelopersSection />} />
        </Route>

        <Route path="your-page">
          <Route index element={<MyPage />} />
        </Route>

        <Route path="services">
          <Route index element={<Services />} />
          <Route path="new" element={<ServiceBuilder />} />
          <Route path=":providerServiceId" element={<ServiceBuilder />} />
        </Route>

        <Route path="integrations" element={<IntegrationsContainer />}>
          <Route path="calendly">
            <Route index element={<CalendlyIntegration />} />
            <Route path="callback" element={<CalendlyIntegrationCallback />} />
          </Route>
          <Route path="stripe">
            <Route index element={<StripeIntegration />} />
            <Route path="callback" element={<StripeIntegrationCallback />} />
          </Route>
          <Route path="calendar">
            <Route index element={<NylasIntegration />} />
            <Route path="callback" element={<NylasIntegrationCallback />} />
          </Route>
        </Route>

        <Route path="billing">
          <Route path="success" element={<BillingSuccessV2 />} />
          <Route path="updated" element={<SubscriptionUpdated />} />
        </Route>

        <Route path="logout" element={<Logout />} />
      </Route>

      <Route path="*" element={<CatchAll />} />
    </SentryRoutes>
  );
};

export default AppRoutes;
